import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients as clientsApi, plans as plansApi, campaigns } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { TruckIcon, NavigateIcon, CheckIcon, CalendarIcon, ClockIcon } from '../common/icons.jsx';

const DEPOT = { lat: 42.54957, lng: 9.471405, name: 'La Ferme Corse, Lucciana' };
const DEFAULT_CAPACITY = 42;

/**
 * Auto-suggest optimized delivery routes from depot.
 * Supports single-day and multi-day planning.
 * When total units exceed capacity, splits across consecutive working days.
 */
export default function PlanSuggest() {
  const navigate = useNavigate();
  const { activeCampaign } = useAuth();
  const [capacity, setCapacity] = useState(DEFAULT_CAPACITY);
  const [pendingClients, setPendingClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [date, setDate] = useState(todayStr());
  const [mode, setMode] = useState('single'); // 'single' or 'multi'
  const [multiDaySuggestions, setMultiDaySuggestions] = useState([]);
  const mapRefs = useRef({});
  const mapInstances = useRef([]);

  useEffect(() => {
    loadData();
    return () => {
      // Cleanup Leaflet maps on unmount
      mapInstances.current.forEach((m) => { try { m.remove(); } catch (_e) {} });
      mapInstances.current = [];
    };
  }, []);

  const loadData = async () => {
    try {
      const [cls, users] = await Promise.all([
        clientsApi.list(),
        campaigns.listUsers(activeCampaign).catch(() => []),
      ]);
      const pending = cls.filter((c) => c.delivery_status === 'pending' && c.lat && c.lng);
      setPendingClients(pending);
      setDrivers(users.filter((u) => u.role === 'driver' || u.role === 'coordinator'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPendingUnits = pendingClients.reduce((s, c) => s + (c.nb_units || 0), 0);
  const needsMultiDay = totalPendingUnits > capacity;

  const generateSuggestions = () => {
    if (pendingClients.length === 0) return;

    if (mode === 'multi') {
      generateMultiDaySuggestions();
      return;
    }

    const routes = [];

    // Route 1: Nearest-neighbor from depot (greedy shortest)
    const route1 = buildNearestNeighborRoute(pendingClients, capacity, DEPOT);
    route1.label = 'Plus court';
    route1.description = 'Clients les plus proches en premier';
    route1.color = '#3B82F6';
    routes.push(route1);

    // Route 2: Farthest-first (cover distant clients first)
    const route2 = buildFarthestFirstRoute(pendingClients, capacity, DEPOT);
    route2.label = 'Éloignés d\'abord';
    route2.description = 'Commence par les clients les plus loin';
    route2.color = '#059669';
    routes.push(route2);

    // Route 3: Geographic cluster (north vs south)
    const remaining = pendingClients.filter(
      (c) => !route1.clients.find((r) => r.id === c.id)
    );
    if (remaining.length > 0) {
      const route3 = buildNearestNeighborRoute(remaining, capacity, DEPOT);
      route3.label = 'Tournée suivante';
      route3.description = 'Clients restants après tournée 1';
      route3.color = '#f97316';
      routes.push(route3);
    }

    setSuggestions(routes);
    setSelectedRoute(0);

    // Render maps after state update
    setTimeout(() => renderAllMaps(routes), 100);
  };

  const generateMultiDaySuggestions = () => {
    const COLORS = ['#3B82F6', '#059669', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6'];
    const remaining = [...pendingClients];
    const days = [];
    let current = DEPOT;

    while (remaining.length > 0) {
      const dayClients = [];
      let dayUnits = 0;
      let pos = current;

      while (remaining.length > 0) {
        let bestIdx = -1;
        let bestDist = Infinity;
        for (let i = 0; i < remaining.length; i++) {
          if (dayUnits + (remaining[i].nb_units || 0) > capacity) continue;
          const d = haversine(pos, remaining[i]);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        if (bestIdx === -1) break;
        const next = remaining.splice(bestIdx, 1)[0];
        dayClients.push(next);
        dayUnits += next.nb_units || 0;
        pos = next;
      }

      if (dayClients.length === 0 && remaining.length > 0) {
        // Single client too big for capacity — add it alone
        const big = remaining.splice(0, 1)[0];
        dayClients.push(big);
        dayUnits = big.nb_units || 0;
      }

      const dayDate = getWorkingDay(date, days.length);
      days.push({
        dayIndex: days.length,
        date: dayDate,
        dateLabel: formatDateFr(dayDate),
        clients: dayClients,
        totalUnits: dayUnits,
        distanceKm: Math.round(totalDistance(dayClients, DEPOT)),
        color: COLORS[days.length % COLORS.length],
      });
      current = DEPOT; // Return to depot each day
    }

    setMultiDaySuggestions(days);
  };

  const renderAllMaps = (routes) => {
    const L = window.L;
    if (!L) return;
    routes.forEach((route, i) => {
      const el = mapRefs.current[i];
      if (!el || el._leaflet_id) return;
      const map = L.map(el, { zoomControl: false, attributionControl: false, dragging: false, scrollWheelZoom: false })
        .setView([42.15, 9.1], 8);
      mapInstances.current.push(map);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        maxZoom: 18, subdomains: 'abcd',
      }).addTo(map);

      // Depot
      const depotIcon = L.divIcon({
        className: '', html: '<div style="width:20px;height:20px;background:#f97316;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;">🏠</div>',
        iconSize: [20, 20], iconAnchor: [10, 10],
      });
      L.marker([DEPOT.lat, DEPOT.lng], { icon: depotIcon }).addTo(map);

      // Client markers
      route.clients.forEach((c, j) => {
        L.circleMarker([c.lat, c.lng], {
          radius: 6, fillColor: route.color, color: '#fff', weight: 2, fillOpacity: 0.9,
        }).bindTooltip(`${j + 1}`, { permanent: true, direction: 'center', className: 'route-label' }).addTo(map);
      });

      // Route line via OSRM (real roads)
      const allStops = [DEPOT, ...route.clients, DEPOT];
      fetchAndDrawOsrmRoute(allStops, route, i, map, L);

      // Fit bounds
      const allPoints = [DEPOT, ...route.clients];
      const bounds = L.latLngBounds(allPoints.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [15, 15] });
    });
  };

  const fetchAndDrawOsrmRoute = async (stops, route, idx, map, L) => {
    const coords = stops.map((s) => `${s.lng},${s.lat}`).join(';');
    try {
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
      if (!res.ok) throw new Error('OSRM error');
      const data = await res.json();
      if (data.routes && data.routes[0]) {
        const osrmRoute = data.routes[0];
        const geojsonCoords = osrmRoute.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        L.polyline(geojsonCoords, { color: route.color, weight: 4, opacity: 0.8 }).addTo(map);

        // Update distance/duration with real values
        setSuggestions((prev) => prev.map((s, i) => i === idx ? {
          ...s,
          distanceKm: Math.round(osrmRoute.distance / 1000),
          durationMin: Math.round(osrmRoute.duration / 60),
        } : s));
      } else {
        // Fallback straight line
        const fallbackCoords = stops.map((s) => [s.lat, s.lng]);
        L.polyline(fallbackCoords, { color: route.color, weight: 3, opacity: 0.7, dashArray: '8, 6' }).addTo(map);
      }
    } catch (_err) {
      const fallbackCoords = stops.map((s) => [s.lat, s.lng]);
      L.polyline(fallbackCoords, { color: route.color, weight: 3, opacity: 0.7, dashArray: '8, 6' }).addTo(map);
    }
  };

  const handleCreate = async () => {
    if (!selectedDriver) return;
    setCreating(true);
    setError('');
    try {
      if (mode === 'multi' && multiDaySuggestions.length > 0) {
        // Create all day plans at once
        const allClientIds = multiDaySuggestions.flatMap((d) => d.clients.map((c) => c.id));
        const plans = await plansApi.createMultiDay({
          startDate: date,
          driverId: selectedDriver,
          clientIds: allClientIds,
          startTime: '08:00',
          vehicleCapacity: capacity,
        });
        // Navigate to first plan
        if (plans.length > 0) navigate(`/plans/${plans[0].id}`);
        else navigate('/plans');
      } else {
        if (selectedRoute === null) return;
        const route = suggestions[selectedRoute];
        const result = await plansApi.create({
          date,
          startTime: '08:00',
          driverId: selectedDriver,
          clientIds: route.clients.map((c) => c.id),
          vehicleCapacity: capacity,
        });
        navigate(`/plans/${result.id}`);
      }
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-[#1e40af] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-[#0f172a]">Suggestion d'itinéraire</h2>
      <p className="text-sm text-[#64748b]">
        {pendingClients.length} clients en attente ({totalPendingUnits} unités) — Départ : {DEPOT.name}
      </p>

      {/* Multi-day banner */}
      {needsMultiDay && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <CalendarIcon size={20} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {totalPendingUnits} unités pour {capacity} de capacité — planification multi-jours recommandée
              </p>
              <p className="text-xs text-amber-600 mt-1">
                ~{Math.ceil(totalPendingUnits / capacity)} jours nécessaires pour livrer tous les clients
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Capacity + Date + Driver */}
      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
        {/* Mode toggle */}
        {needsMultiDay && (
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            <button type="button" onClick={() => { setMode('single'); setSuggestions([]); setMultiDaySuggestions([]); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === 'single' ? 'bg-white text-[#1e40af] shadow-sm' : 'text-[#64748b]'}`}>
              Journée unique
            </button>
            <button type="button" onClick={() => { setMode('multi'); setSuggestions([]); setMultiDaySuggestions([]); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === 'multi' ? 'bg-white text-[#1e40af] shadow-sm' : 'text-[#64748b]'}`}>
              Multi-jours
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">Capacité (vélos)</label>
            <input
              type="number" min={1} max={200} value={capacity}
              onChange={(e) => setCapacity(parseInt(e.target.value) || 1)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">
              {mode === 'multi' ? 'Date de début' : 'Date'}
            </label>
            <input
              type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#64748b] mb-1">Livreur</label>
          <select
            value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
          >
            <option value="">-- Choisir un livreur --</option>
            {drivers.map((d) => (
              <option key={d.userId || d.id} value={d.userId || d.id}>{d.name || d.userName}</option>
            ))}
          </select>
        </div>
        <button
          onClick={generateSuggestions}
          disabled={pendingClients.length === 0}
          className="w-full bg-gradient-to-r from-[#1e40af] to-[#0891b2] text-white rounded-xl py-3 font-semibold text-sm disabled:opacity-50"
        >
          <TruckIcon size={16} className="inline mr-2" />
          {mode === 'multi' ? 'Planifier sur plusieurs jours' : 'Suggérer les itinéraires'}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      {/* Step 2: Route suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-3">
          {suggestions.map((route, i) => (
            <div
              key={i}
              onClick={() => setSelectedRoute(i)}
              className={`bg-white rounded-2xl overflow-hidden shadow-sm cursor-pointer transition-all ${
                selectedRoute === i ? 'ring-2 ring-[#1e40af] shadow-md' : 'hover:shadow-md'
              }`}
            >
              {/* Mini map */}
              <div
                ref={(el) => { mapRefs.current[i] = el; }}
                className="w-full h-40"
              />
              {/* Route info */}
              <div className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ background: route.color }} />
                    <span className="font-bold text-sm text-[#0f172a]">{route.label}</span>
                    {selectedRoute === i && (
                      <span className="bg-[#1e40af] text-white text-[10px] px-2 py-0.5 rounded-full">Sélectionné</span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#64748b] mb-2">{route.description}</p>
                <div className="flex gap-4 text-xs">
                  <span className="font-medium">{route.clients.length} clients</span>
                  <span className="font-medium">{route.totalUnits} unités</span>
                  <span className="font-medium">{route.distanceKm} km</span>
                  {route.durationMin && <span className="font-medium">~{route.durationMin} min</span>}
                </div>
                {/* Client list */}
                <div className="mt-2 flex flex-wrap gap-1">
                  {route.clients.map((c, j) => (
                    <span key={c.id} className="text-[10px] bg-gray-100 text-[#64748b] px-2 py-0.5 rounded">
                      {j + 1}. {c.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 3: Create (single-day) */}
      {mode === 'single' && suggestions.length > 0 && selectedRoute !== null && (
        <button
          onClick={handleCreate}
          disabled={creating || !selectedDriver}
          className="w-full bg-[#059669] hover:bg-[#047857] text-white rounded-xl py-4 font-bold text-sm disabled:opacity-50 min-h-[52px]"
        >
          {creating ? 'Création...' : !selectedDriver ? 'Choisir un livreur' : (
            <>
              <CheckIcon size={16} className="inline mr-2" />
              Créer la tournée ({suggestions[selectedRoute].clients.length} clients, {suggestions[selectedRoute].totalUnits} unités)
            </>
          )}
        </button>
      )}

      {/* Multi-day suggestions */}
      {mode === 'multi' && multiDaySuggestions.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-[#0f172a]">
            Répartition sur {multiDaySuggestions.length} jour{multiDaySuggestions.length > 1 ? 's' : ''}
          </h3>
          {multiDaySuggestions.map((day) => (
            <div key={day.dayIndex}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border-l-4"
              style={{ borderLeftColor: day.color }}>
              <div className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ background: day.color }}>
                      J{day.dayIndex + 1}
                    </span>
                    <div>
                      <span className="font-bold text-sm text-[#0f172a]">{day.dateLabel}</span>
                      <p className="text-xs text-[#64748b]">
                        {day.clients.length} client{day.clients.length > 1 ? 's' : ''} — {day.totalUnits} unités — ~{day.distanceKm} km
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{
                        width: `${Math.min((day.totalUnits / capacity) * 100, 100)}%`,
                        background: day.color,
                      }} />
                    </div>
                    <span className="text-[10px] text-[#64748b]">{Math.round((day.totalUnits / capacity) * 100)}%</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {day.clients.map((c, j) => (
                    <span key={c.id} className="text-[10px] bg-gray-100 text-[#64748b] px-2 py-0.5 rounded">
                      {j + 1}. {c.name} ({c.nb_units || 0}u)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Multi-day create button */}
          <button
            onClick={handleCreate}
            disabled={creating || !selectedDriver}
            className="w-full bg-[#059669] hover:bg-[#047857] text-white rounded-xl py-4 font-bold text-sm disabled:opacity-50 min-h-[52px]"
          >
            {creating ? 'Création en cours...' : !selectedDriver ? 'Choisir un livreur' : (
              <>
                <CheckIcon size={16} className="inline mr-2" />
                Créer {multiDaySuggestions.length} tournées ({pendingClients.length} clients)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Route building algorithms ──

/** Haversine distance in km */
function haversine(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/** Total route distance (depot → clients → depot) */
function totalDistance(clients, depot) {
  if (clients.length === 0) return 0;
  let dist = haversine(depot, clients[0]);
  for (let i = 1; i < clients.length; i++) {
    dist += haversine(clients[i - 1], clients[i]);
  }
  dist += haversine(clients[clients.length - 1], depot);
  return dist;
}

/** Nearest-neighbor from depot, filling up to capacity */
function buildNearestNeighborRoute(clients, capacity, depot) {
  const remaining = [...clients];
  const selected = [];
  let units = 0;
  let current = depot;

  while (remaining.length > 0) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      if (units + (remaining[i].nb_units || 0) > capacity) continue;
      const d = haversine(current, remaining[i]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    if (bestIdx === -1) break;
    const next = remaining.splice(bestIdx, 1)[0];
    selected.push(next);
    units += next.nb_units || 0;
    current = next;
  }

  const dist = totalDistance(selected, depot);
  return {
    clients: selected,
    totalUnits: units,
    distanceKm: Math.round(dist),
  };
}

/** Farthest-first: start with most distant client, then nearest-neighbor */
function buildFarthestFirstRoute(clients, capacity, depot) {
  if (clients.length === 0) return { clients: [], totalUnits: 0, distanceKm: 0 };

  // Find farthest client from depot
  let farthestIdx = 0;
  let farthestDist = 0;
  clients.forEach((c, i) => {
    const d = haversine(depot, c);
    if (d > farthestDist) { farthestDist = d; farthestIdx = i; }
  });

  // Start with farthest, then nearest-neighbor back toward depot
  const remaining = [...clients];
  const first = remaining.splice(farthestIdx, 1)[0];
  const selected = [first];
  let units = first.nb_units || 0;
  let current = first;

  while (remaining.length > 0) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      if (units + (remaining[i].nb_units || 0) > capacity) continue;
      const d = haversine(current, remaining[i]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    if (bestIdx === -1) break;
    const next = remaining.splice(bestIdx, 1)[0];
    selected.push(next);
    units += next.nb_units || 0;
    current = next;
  }

  const dist = totalDistance(selected, depot);
  return {
    clients: selected,
    totalUnits: units,
    distanceKm: Math.round(dist),
  };
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

/** Get Nth working day from a start date (skip weekends) */
function getWorkingDay(startDate, offset) {
  const d = new Date(startDate + 'T00:00:00');
  let added = 0;
  while (added < offset) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d.toISOString().split('T')[0];
}

/** Format YYYY-MM-DD to French date */
function formatDateFr(dateStr) {
  const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const d = new Date(dateStr + 'T00:00:00');
  return `${DAYS[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}
